import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { PackageOpen, RefreshCw, Loader, Check, Trash2, Unlock, MapPin, AlertTriangle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { putAwayService } from '../../../../services/putAwayService';
import {
  StcIntRelOrderDto,
  StcIntRelOrderDetailsDto,
  StcIntRelOrderLineDto,
  StcIntRelOrderStatus,
  NEW_PUTAWAY_ORDER_STATUSES,
} from '../../../../types/putaway';
import { BackButton } from '../../../common/BackButton';
import { StatusBadge } from '../../../common/StatusBadge';
import { useStatusOptions } from '../../../common/statusPresentation';
import { LocationSearchModal, LocationSearchResult } from '../../../common/LocationSearchModal';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { WarehouseTable, createColumnFactory, formatDateTime } from '../../../shared/table';
import type { ColumnDefinition, ExpandableRowConfig, TableDataRow, RowAction } from '../../../shared/table';

/**
 * Plasseringsordre (lagersjef) — planleggerbilde for StcIntRelOrder, TW 36401824.
 *
 * Viser alle åpne plasseringsordre (OrderStatus 0 manuell, 10 systemgenerert, 20 godkjent)
 * med ekspandering til linjer, etter samme lest som «Plukklister under arbeid».
 * Handlinger: godkjenn (0/10 → 20), endre prioritet, bytt mål-lokasjon på linje, lås opp, slett.
 * Godkjenning er en ren statusovergang — ingen regelkjøring (regelmotorens auto-godkjenning
 * kjører kun under generering).
 */

/** StatusCodes-typer brukt i bildet. */
const STATUS_TYPE_ORDER = 26;      // StcIntRelOrder.OrderStatus
const STATUS_TYPE_TRANSTYPE = 25;  // StcIntRelOrder.TransType
const STATUS_TYPE_LINE = 40;       // StcIntRelOrderLine.LineStatus

const OPEN_ORDER_STATUSES: readonly number[] = [
  ...NEW_PUTAWAY_ORDER_STATUSES,
  StcIntRelOrderStatus.GODKJENT,
];

interface PutAwayOrderRow extends TableDataRow {
  id: number;
  stcOrderNo: number;
  orderStatus: number;
  transType: number;
  warehouseID: string;
  transDt: string;
  orderPriority: number;
  lockedBy: string | null;
  operatorID: string | null;
  description1: string | null;
  created: string | null;
  createdBy: string | null;
  _original: StcIntRelOrderDto;
}

interface LocationTarget {
  stcOrderNo: number;
  warehouseID: string;
  line: StcIntRelOrderLineDto;
}

interface PutAwayOrdersProps {
  onBack?: () => void;
}

const PutAwayOrders: React.FC<PutAwayOrdersProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouse();

  const [orders, setOrders] = useState<StcIntRelOrderDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);

  // Detaljer (linjer) per ordre — lastes ved ekspandering
  const [details, setDetails] = useState<Map<number, StcIntRelOrderDetailsDto>>(new Map());
  const [loadingDetails, setLoadingDetails] = useState<Set<number>>(new Set());

  // Pågående handlinger per ordre (godkjenn / lås opp / slett / prioritet)
  const [busyOrders, setBusyOrders] = useState<Set<number>>(new Set());
  const [savedOrders, setSavedOrders] = useState<Set<number>>(new Set());

  // Sletting
  const [orderToDelete, setOrderToDelete] = useState<StcIntRelOrderDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Bytt lokasjon på linje
  const [locationTarget, setLocationTarget] = useState<LocationTarget | null>(null);
  const [changingLocation, setChangingLocation] = useState(false);

  // Utkast for prioritet (ukontrollert input — verdien leses ved blur/Enter)
  const priorityDraft = useRef<Record<number, number>>({});
  const ordersRef = useRef<StcIntRelOrderDto[]>([]);
  useEffect(() => { ordersRef.current = orders; }, [orders]);

  const orderStatusOptions = useStatusOptions(STATUS_TYPE_ORDER);
  const transTypeOptions = useStatusOptions(STATUS_TYPE_TRANSTYPE);

  const columnFactory = useMemo(() => createColumnFactory<PutAwayOrderRow>(), []);

  // ---------------------------------------------------------------------------
  // Lasting
  // ---------------------------------------------------------------------------

  const loadOrders = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const data = await putAwayService.getPutAwayOrdersByStatuses(OPEN_ORDER_STATUSES, {
        warehouseId: currentWarehouse || undefined,
      });
      setOrders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('manager.modules.putAwayOrders.errors.loadFailed', {}, 'Kunne ikke laste plasseringsordre'));
    } finally {
      setLoading(false);
    }
  }, [currentWarehouse, t]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const loadDetails = useCallback(async (stcOrderNo: number) => {
    setLoadingDetails(prev => new Set(prev).add(stcOrderNo));
    try {
      const d = await putAwayService.getPutAwayOrderDetails(stcOrderNo);
      setDetails(prev => new Map(prev).set(stcOrderNo, d));
      return d;
    } catch (err) {
      console.error('Kunne ikke laste linjer for plasseringsordre', stcOrderNo, err);
      return undefined;
    } finally {
      setLoadingDetails(prev => {
        const next = new Set(prev);
        next.delete(stcOrderNo);
        return next;
      });
    }
  }, []);

  /** Henter ordren på nytt (hode + linjer) og oppdaterer både rad og detaljpanel. */
  const refreshOrder = useCallback(async (stcOrderNo: number) => {
    const d = await loadDetails(stcOrderNo);
    if (d?.header) {
      setOrders(prev => prev.map(o => (o.stcOrderNo === stcOrderNo ? { ...o, ...d.header } : o)));
    }
  }, [loadDetails]);

  const flashNotice = useCallback((message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(current => (current === message ? null : current)), 5000);
  }, []);

  const withBusy = useCallback(async (stcOrderNo: number, work: () => Promise<void>) => {
    setBusyOrders(prev => new Set(prev).add(stcOrderNo));
    try {
      await work();
    } finally {
      setBusyOrders(prev => {
        const next = new Set(prev);
        next.delete(stcOrderNo);
        return next;
      });
    }
  }, []);

  const markSaved = useCallback((stcOrderNo: number) => {
    setSavedOrders(prev => new Set(prev).add(stcOrderNo));
    window.setTimeout(() => {
      setSavedOrders(prev => {
        const next = new Set(prev);
        next.delete(stcOrderNo);
        return next;
      });
    }, 2000);
  }, []);

  // ---------------------------------------------------------------------------
  // Handlinger på ordre
  // ---------------------------------------------------------------------------

  const handleApprove = useCallback(async (order: StcIntRelOrderDto) => {
    await withBusy(order.stcOrderNo, async () => {
      try {
        await putAwayService.approvePutAwayOrder(order, details.get(order.stcOrderNo)?.lines);
        await refreshOrder(order.stcOrderNo);
        flashNotice(t('manager.modules.putAwayOrders.messages.approved', { '0': order.stcOrderNo }, `Plasseringsordre #${order.stcOrderNo} er godkjent`));
      } catch (err) {
        alert(err instanceof Error ? err.message : t('manager.modules.putAwayOrders.errors.approveFailed', {}, 'Kunne ikke godkjenne plasseringsordre'));
      }
    });
  }, [details, refreshOrder, flashNotice, withBusy, t]);

  const handleUnlock = useCallback(async (order: StcIntRelOrderDto) => {
    const lockedBy = order.lockedBy || '';
    const confirmed = window.confirm(
      t('manager.modules.putAwayOrders.messages.confirmUnlock', { '0': String(order.stcOrderNo), '1': lockedBy },
        `Plasseringsordre #${order.stcOrderNo} er låst til ${lockedBy}. Vil du låse den opp?`)
    );
    if (!confirmed) return;

    await withBusy(order.stcOrderNo, async () => {
      try {
        await putAwayService.unlockPutAwayOrder(order.stcOrderNo);
        await refreshOrder(order.stcOrderNo);
        flashNotice(t('manager.modules.putAwayOrders.messages.unlocked', { '0': order.stcOrderNo }, `Plasseringsordre #${order.stcOrderNo} er låst opp`));
      } catch (err) {
        alert(err instanceof Error ? err.message : t('manager.modules.putAwayOrders.errors.unlockFailed', {}, 'Kunne ikke låse opp plasseringsordre'));
      }
    });
  }, [refreshOrder, flashNotice, withBusy, t]);

  const handleConfirmDelete = useCallback(async () => {
    if (!orderToDelete) return;
    const stcOrderNo = orderToDelete.stcOrderNo;
    setDeleting(true);
    try {
      const result = await putAwayService.deletePutAwayOrder(stcOrderNo);
      setOrders(prev => prev.filter(o => o.stcOrderNo !== stcOrderNo));
      setDetails(prev => {
        const next = new Map(prev);
        next.delete(stcOrderNo);
        return next;
      });
      setOrderToDelete(null);
      flashNotice(
        result.releasedGrunnlagCount > 0
          ? t('manager.modules.putAwayOrders.messages.deletedWithGrunnlag', { '0': stcOrderNo, '1': result.releasedGrunnlagCount },
              `Plasseringsordre #${stcOrderNo} er slettet. ${result.releasedGrunnlagCount} grunnlag er frigitt.`)
          : t('manager.modules.putAwayOrders.messages.deleted', { '0': stcOrderNo }, `Plasseringsordre #${stcOrderNo} er slettet`)
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : t('manager.modules.putAwayOrders.errors.deleteFailed', {}, 'Kunne ikke slette plasseringsordre'));
    } finally {
      setDeleting(false);
    }
  }, [orderToDelete, flashNotice, t]);

  const savePriority = useCallback(async (stcOrderNo: number) => {
    const order = ordersRef.current.find(o => o.stcOrderNo === stcOrderNo);
    const draft = priorityDraft.current[stcOrderNo];
    if (!order || draft === undefined || draft === order.orderPriority) return;

    await withBusy(stcOrderNo, async () => {
      try {
        const updated = await putAwayService.updatePutAwayOrderPriority(order, draft);
        setOrders(prev => prev.map(o => (o.stcOrderNo === stcOrderNo ? { ...o, ...updated } : o)));
        markSaved(stcOrderNo);
      } catch (err) {
        delete priorityDraft.current[stcOrderNo];
        alert(err instanceof Error ? err.message : t('manager.modules.putAwayOrders.errors.priorityFailed', {}, 'Kunne ikke endre prioritet'));
      }
    });
  }, [withBusy, markSaved, t]);

  const handleLocationSelected = useCallback(async (location: LocationSearchResult) => {
    if (!locationTarget) return;
    const { stcOrderNo, line } = locationTarget;
    setChangingLocation(true);
    try {
      await putAwayService.updateLineDestination(stcOrderNo, line.stcLineNo, location.binCode);
      await refreshOrder(stcOrderNo);
      setLocationTarget(null);
      flashNotice(t('manager.modules.putAwayOrders.messages.locationChanged', { '0': line.stcLineNo, '1': location.binCode },
        `Linje ${line.stcLineNo} har fått ny lokasjon ${location.binCode}`));
    } catch (err) {
      alert(err instanceof Error ? err.message : t('manager.modules.putAwayOrders.errors.locationFailed', {}, 'Kunne ikke endre lokasjon'));
    } finally {
      setChangingLocation(false);
    }
  }, [locationTarget, refreshOrder, flashNotice, t]);

  // ---------------------------------------------------------------------------
  // Tabell
  // ---------------------------------------------------------------------------

  const tableData: PutAwayOrderRow[] = useMemo(() => orders.map(o => ({
    id: o.stcOrderNo,
    stcOrderNo: o.stcOrderNo,
    orderStatus: o.orderStatus,
    transType: o.transType,
    warehouseID: o.warehouseID,
    transDt: o.transDt,
    orderPriority: o.orderPriority,
    lockedBy: o.lockedBy?.trim() ? o.lockedBy : null,
    operatorID: o.operatorID || null,
    description1: o.description1 || null,
    created: o.created || null,
    createdBy: o.createdBy || null,
    _original: o,
  })), [orders]);

  const getRowId = useCallback((row: PutAwayOrderRow) => row.stcOrderNo, []);

  const canApprove = (row: PutAwayOrderRow) => NEW_PUTAWAY_ORDER_STATUSES.includes(row.orderStatus);
  const isOpen = (row: PutAwayOrderRow) => row.orderStatus < StcIntRelOrderStatus.AVSLUTTET;

  const renderPriorityCell = useCallback((value: number, row: PutAwayOrderRow) => {
    const stcOrderNo = row.stcOrderNo;
    const stop = (e: React.SyntheticEvent) => e.stopPropagation();
    const busy = busyOrders.has(stcOrderNo);
    const saved = savedOrders.has(stcOrderNo);

    return (
      <div className="flex items-center space-x-1" onClick={stop} onMouseDown={stop}>
        <input
          key={`priority-${stcOrderNo}-${value}`}
          type="number"
          defaultValue={value}
          min={0}
          max={999}
          disabled={busy || !isOpen(row)}
          onChange={(e) => {
            e.stopPropagation();
            priorityDraft.current[stcOrderNo] = parseInt(e.target.value, 10) || 0;
          }}
          onBlur={() => savePriority(stcOrderNo)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              e.preventDefault();
              savePriority(stcOrderNo);
              (e.target as HTMLInputElement).blur();
            }
          }}
          onClick={stop}
          onMouseDown={stop}
          onFocus={stop}
          className="w-16 px-2 py-1 text-xs text-right border border-gray-300 rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white disabled:opacity-60"
        />
        {busy && <Loader size={12} className="animate-spin text-gray-400" />}
        {saved && <Check size={14} className="text-green-600" />}
      </div>
    );
  }, [busyOrders, savedOrders, savePriority]);

  const columns: ColumnDefinition<PutAwayOrderRow>[] = useMemo(() => [
    columnFactory.createNumberColumn('stcOrderNo', 'stcOrderNo', t('manager.modules.putAwayOrders.table.orderNo', {}, 'Ordrenr.'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => (value !== null ? String(value) : '-'),
    }),
    columnFactory.createTextColumn('orderStatus', 'orderStatus', t('common.fields.status', {}, 'Status'), {
      width: 150,
      sortable: true,
      filterType: 'dropdown',
      filterOptions: orderStatusOptions.map(o => ({ value: String(o.value), label: o.label })),
      renderer: (_value, row) => <StatusBadge value={row.orderStatus} statusType={STATUS_TYPE_ORDER} />,
    }),
    columnFactory.createTextColumn('transType', 'transType', t('manager.modules.putAwayOrders.table.transType', {}, 'Type'), {
      width: 120,
      sortable: true,
      filterType: 'dropdown',
      filterOptions: transTypeOptions.map(o => ({ value: String(o.value), label: o.label })),
      renderer: (_value, row) => <StatusBadge value={row.transType} statusType={STATUS_TYPE_TRANSTYPE} />,
    }),
    columnFactory.createNumberColumn('orderPriority', 'orderPriority', t('manager.modules.putAwayOrders.table.priority', {}, 'Prioritet'), {
      width: 100,
      sortable: true,
      filterType: 'text',
      renderer: renderPriorityCell,
    }),
    columnFactory.createTextColumn('lockedBy', 'lockedBy', t('manager.modules.putAwayOrders.table.lockedBy', {}, 'Låst av'), {
      width: 130,
      sortable: true,
      filterType: 'dropdown',
      enableDropdownFilter: true,
      formatter: (value) => value || '-',
    }),
    // Merk: lineCount fylles ikke av liste-endepunktene (kun når linjer er lastet), så
    // antall linjer vises i det ekspanderte panelet i stedet for som kolonne.
    columnFactory.createTextColumn('warehouseID', 'warehouseID', t('warehouse.general.warehouse', {}, 'Lager'), {
      width: 90,
      sortable: true,
      filterType: 'dropdown',
      enableDropdownFilter: true,
    }),
    columnFactory.createTextColumn('operatorID', 'operatorID', t('manager.modules.putAwayOrders.table.operator', {}, 'Operatør'), {
      width: 120,
      sortable: true,
      filterType: 'dropdown',
      enableDropdownFilter: true,
      formatter: (value) => value || '-',
    }),
    columnFactory.createTextColumn('description1', 'description1', t('common.fields.description', {}, 'Beskrivelse'), {
      width: 220,
      sortable: true,
      filterType: 'text',
      formatter: (value) => value || '-',
    }),
    columnFactory.createDateColumn('transDt', 'transDt', t('manager.modules.putAwayOrders.table.transDt', {}, 'Ordredato'), {
      width: 140,
      sortable: true,
      filterType: 'date',
      formatter: (value) => (value ? formatDateTime(value) : '-'),
    }),
    columnFactory.createDateColumn('created', 'created', t('manager.modules.putAwayOrders.table.created', {}, 'Opprettet'), {
      width: 140,
      sortable: true,
      visible: false,
      filterType: 'date',
      formatter: (value) => (value ? formatDateTime(value) : '-'),
    }),
    columnFactory.createTextColumn('createdBy', 'createdBy', t('manager.modules.putAwayOrders.table.createdBy', {}, 'Opprettet av'), {
      width: 120,
      sortable: true,
      visible: false,
      filterType: 'text',
      formatter: (value) => value || '-',
    }),
  ], [columnFactory, orderStatusOptions, transTypeOptions, renderPriorityCell, t]);

  const rowActions: RowAction<PutAwayOrderRow>[] = useMemo(() => [
    {
      id: 'approve',
      label: t('manager.modules.putAwayOrders.actions.approve', {}, 'Godkjenn'),
      icon: Check,
      variant: 'primary' as const,
      disabled: (row) => !canApprove(row) || busyOrders.has(row.stcOrderNo),
      onClick: (row) => handleApprove(row._original),
    },
    {
      id: 'unlock',
      label: t('manager.modules.putAwayOrders.actions.unlock', {}, 'Lås opp'),
      icon: Unlock,
      variant: 'secondary' as const,
      disabled: (row) => !row.lockedBy || busyOrders.has(row.stcOrderNo),
      onClick: (row) => handleUnlock(row._original),
    },
    {
      id: 'delete',
      label: t('manager.modules.putAwayOrders.actions.delete', {}, 'Slett'),
      icon: Trash2,
      variant: 'danger' as const,
      disabled: (row) => !isOpen(row) || busyOrders.has(row.stcOrderNo),
      onClick: (row) => setOrderToDelete(row._original),
    },
  ], [busyOrders, handleApprove, handleUnlock, t]);

  // ---------------------------------------------------------------------------
  // Ekspanderte linjer
  // ---------------------------------------------------------------------------

  const renderLines = useCallback((row: PutAwayOrderRow, d: StcIntRelOrderDetailsDto | undefined) => {
    if (loadingDetails.has(row.stcOrderNo)) {
      return (
        <div className="flex items-center justify-center py-8 bg-slate-50">
          <Loader className="animate-spin mr-2" size={20} />
          <span className="text-gray-600">{t('manager.modules.putAwayOrders.lines.loadingLines', {}, 'Laster linjer...')}</span>
        </div>
      );
    }

    if (!d) {
      return (
        <div className="text-center py-8 text-gray-500 bg-slate-50">
          {t('manager.modules.putAwayOrders.lines.couldNotLoadLines', {}, 'Kunne ikke laste linjer')}
        </div>
      );
    }

    const orderIsOpen = isOpen(row);
    const unresolvedCount = d.lines.filter(l => putAwayService.isUnresolvedLine(l)).length;

    return (
      <div className="bg-slate-50 p-3 border-t border-slate-200">
        <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
          <PackageOpen size={16} />
          <span>{t('manager.modules.putAwayOrders.lines.linesForOrder', { '0': row.stcOrderNo }, `Linjer for plasseringsordre ${row.stcOrderNo}`)}</span>
          <span className="text-xs text-gray-500">
            ({t('manager.modules.putAwayOrders.lines.completedSummary', { '0': d.completedLines, '1': d.totalLines }, `${d.completedLines} av ${d.totalLines} plassert`)})
          </span>
          {unresolvedCount > 0 && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">
              <AlertTriangle size={12} />
              {t('manager.modules.putAwayOrders.lines.unresolvedCount', { '0': unresolvedCount }, `${unresolvedCount} uten lokasjon`)}
            </span>
          )}
        </h4>

        {d.lines.length > 0 ? (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100">
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.putAwayOrders.lines.lineNo', {}, 'Linje')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.putAwayOrders.lines.itemNo', {}, 'Varenr')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500 whitespace-nowrap">{t('common.fields.quantity', {}, 'Antall')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500 whitespace-nowrap">{t('manager.modules.putAwayOrders.lines.moved', {}, 'Plassert')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500 whitespace-nowrap">{t('manager.modules.putAwayOrders.lines.remaining', {}, 'Gjenstår')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.putAwayOrders.lines.fromBin', {}, 'Fra lokasjon')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.putAwayOrders.lines.toBin', {}, 'Til lokasjon')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('common.fields.status', {}, 'Status')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.putAwayOrders.lines.movedBy', {}, 'Plassert av')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {d.lines.map((line) => {
                const unresolved = putAwayService.isUnresolvedLine(line);
                const canChange = orderIsOpen && !line.isCompleted;
                return (
                  <tr key={line.id} className="hover:bg-slate-100 transition-colors">
                    <td className="py-2 px-3 text-slate-900 font-medium">{line.stcLineNo}</td>
                    <td className="py-2 px-3 text-slate-900 font-mono">{line.itemNo || '-'}</td>
                    <td className="py-2 px-3 text-slate-900">{line.description1 || '-'}</td>
                    <td className="py-2 px-3 text-slate-900 text-right whitespace-nowrap tabular-nums">{line.quantity.toFixed(2)}</td>
                    <td className="py-2 px-3 text-slate-900 text-right whitespace-nowrap tabular-nums">{(line.quantityMoved ?? 0).toFixed(2)}</td>
                    <td className="py-2 px-3 text-slate-900 text-right whitespace-nowrap tabular-nums font-medium">{line.remainingQuantity.toFixed(2)}</td>
                    <td className="py-2 px-3 text-slate-900">{line.fromBinCode || '-'}</td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        {unresolved ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">
                            <AlertTriangle size={12} />
                            {t('manager.modules.putAwayOrders.lines.unresolved', {}, '[X] mangler lokasjon')}
                          </span>
                        ) : (
                          <span className="text-slate-900">{line.toBinCode || '-'}</span>
                        )}
                        {canChange && (
                          <button
                            type="button"
                            onClick={() => setLocationTarget({ stcOrderNo: row.stcOrderNo, warehouseID: row.warehouseID, line })}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                            title={t('manager.modules.putAwayOrders.actions.changeLocationTooltip', {}, 'Overstyr foreslått mål-lokasjon for linjen')}
                          >
                            <MapPin size={12} />
                            {t('manager.modules.putAwayOrders.actions.changeLocation', {}, 'Bytt lokasjon')}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3"><StatusBadge value={line.lineStatus} statusType={STATUS_TYPE_LINE} /></td>
                    <td className="py-2 px-3 text-slate-900">{line.movedBy || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {t('manager.modules.putAwayOrders.lines.noLines', {}, 'Ingen linjer på denne plasseringsordren')}
          </div>
        )}
      </div>
    );
  }, [loadingDetails, t]);

  const expandableConfig: ExpandableRowConfig<PutAwayOrderRow, StcIntRelOrderDetailsDto | undefined> = useMemo(() => ({
    getDetailData: (row) => details.get(row.stcOrderNo),
    renderDetail: renderLines,
    expandOnRowClick: false,
    onExpand: async (row) => {
      if (!details.has(row.stcOrderNo)) {
        await loadDetails(row.stcOrderNo);
      }
    },
  }), [details, renderLines, loadDetails]);

  const tableActions = useMemo(() => [
    {
      id: 'refresh',
      label: t('common.buttons.refresh', {}, 'Oppdater'),
      icon: RefreshCw,
      onClick: () => loadOrders(true),
      variant: 'secondary' as const,
    },
  ], [loadOrders, t]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="animate-spin" size={20} />
          <span>{t('manager.modules.putAwayOrders.loading', {}, 'Laster plasseringsordre...')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
        <h3 className="text-red-800 font-medium">{t('manager.modules.putAwayOrders.errors.loadFailed', {}, 'Kunne ikke laste plasseringsordre')}</h3>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={() => loadOrders()}
          className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center space-x-2"
        >
          <RefreshCw size={16} />
          <span>{t('manager.modules.putAwayOrders.tryAgain', {}, 'Prøv igjen')}</span>
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-4 pb-2 bg-white border-b">
        <div className="flex items-center space-x-4">
          <BackButton onClick={onBack || (() => {})} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <PackageOpen size={24} />
            <span>{t('manager.modules.putAwayOrders.title', {}, 'Plasseringsordre')}</span>
          </h1>
          <span className="text-sm text-gray-500">
            {t('manager.modules.putAwayOrders.listCount', { '0': tableData.length }, `${tableData.length} plasseringsordre`)}
          </span>
          {notice && (
            <span className="ml-auto inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-lg bg-green-100 text-green-800">
              <Check size={14} />
              {notice}
            </span>
          )}
        </div>

        {/* Info toggle */}
        <button
          type="button"
          onClick={() => setShowInfo(prev => !prev)}
          className="mt-2 flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
          aria-expanded={showInfo}
        >
          <Info size={14} />
          <span>{t('manager.modules.putAwayOrders.infoToggleLabel', {}, 'Hva er Plasseringsordre?')}</span>
          {showInfo ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {showInfo && (
          <div className="mt-2 text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-2">
            <p>{t('manager.modules.putAwayOrders.purposeBody', {}, 'Plasseringsordre viser alle åpne utleggs- og påfyllingsordre — fra de er opprettet til de er ferdig plassert. Herfra godkjenner en lagersjef ordre som venter, endrer prioritet, overstyrer foreslått lokasjon på linjer, låser opp ordre som henger hos en operatør, og sletter ordre som ikke skal utføres.')}</p>
            <p>{t('manager.modules.putAwayOrders.criteriaIntro', {}, 'En ordre vises i listen når den har én av disse statusene:')}</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li><StatusBadge value={StcIntRelOrderStatus.MANUELL} statusType={STATUS_TYPE_ORDER} />{t('manager.modules.putAwayOrders.criteriaStatus0Suffix', {}, ' — opprettet manuelt, må godkjennes før operatør ser den')}</li>
              <li><StatusBadge value={StcIntRelOrderStatus.SYSTEMGENERERT} statusType={STATUS_TYPE_ORDER} />{t('manager.modules.putAwayOrders.criteriaStatus10Suffix', {}, ' — generert av regelmotoren, ikke godkjent (ofte fordi lokasjon mangler)')}</li>
              <li><StatusBadge value={StcIntRelOrderStatus.GODKJENT} statusType={STATUS_TYPE_ORDER} />{t('manager.modules.putAwayOrders.criteriaStatus20Suffix', {}, ' — godkjent og synlig for operatør i Plassering')}</li>
            </ul>
            <p>{t('manager.modules.putAwayOrders.approveNote', {}, 'Godkjenning krever at minst én linje har en reell mål-lokasjon. Linjer merket [X] må få lokasjon først. Sletting er bare mulig så lenge ingen linjer er plassert; grunnlaget frigis da tilbake til Grunnlag-skjermen.')}</p>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 px-4 py-2">
        <WarehouseTable
          data={tableData}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: true,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            allowRowSelection: false,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'putaway-orders-table',
          }}
          getRowId={getRowId}
          rowActions={rowActions}
          tableActions={tableActions}
          expandableConfig={expandableConfig}
          loading={false}
          error={undefined}
          aria-label={t('manager.modules.putAwayOrders.tableAriaLabel', {}, 'Plasseringsordre tabell')}
        />
      </div>

      {/* Bekreft sletting */}
      {orderToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-red-600 text-white px-6 py-4 rounded-t-lg flex items-center space-x-3">
              <Trash2 size={24} />
              <h2 className="text-xl font-semibold">{t('manager.modules.putAwayOrders.delete.confirmTitle', {}, 'Bekreft sletting')}</h2>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">
                {t('manager.modules.putAwayOrders.delete.confirmQuestionPrefix', {}, 'Er du sikker på at du vil slette plasseringsordre ')}<strong>#{orderToDelete.stcOrderNo}</strong>?
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">{t('common.fields.status', {}, 'Status')}:</div>
                  <div className="text-gray-900"><StatusBadge value={orderToDelete.orderStatus} statusType={STATUS_TYPE_ORDER} /></div>
                  <div className="text-gray-500">{t('manager.modules.putAwayOrders.table.transType', {}, 'Type')}:</div>
                  <div className="text-gray-900"><StatusBadge value={orderToDelete.transType} statusType={STATUS_TYPE_TRANSTYPE} /></div>
                  <div className="text-gray-500">{t('manager.modules.putAwayOrders.table.lockedBy', {}, 'Låst av')}:</div>
                  <div className="text-gray-900">{orderToDelete.lockedBy?.trim() || '-'}</div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                <strong>{t('manager.modules.putAwayOrders.delete.warningLabel', {}, 'Merk:')}</strong> {t('manager.modules.putAwayOrders.delete.warningBody', {}, 'Ordren og alle linjene slettes. Grunnlaget frigis tilbake til Grunnlag-skjermen. Ordre med plasserte linjer kan ikke slettes.')}
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end space-x-3">
              <button
                onClick={() => !deleting && setOrderToDelete(null)}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center space-x-2 disabled:opacity-50"
              >
                {deleting ? <Loader size={16} className="animate-spin" /> : <Trash2 size={16} />}
                <span>{t('manager.modules.putAwayOrders.actions.delete', {}, 'Slett')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bytt lokasjon på linje */}
      <LocationSearchModal
        isOpen={!!locationTarget}
        onClose={() => !changingLocation && setLocationTarget(null)}
        onSelect={handleLocationSelected}
        title={locationTarget
          ? t('manager.modules.putAwayOrders.changeLocationTitle', { '0': locationTarget.line.stcLineNo, '1': locationTarget.line.itemNo },
              `Bytt lokasjon — linje ${locationTarget.line.stcLineNo} (${locationTarget.line.itemNo})`)
          : t('manager.modules.putAwayOrders.actions.changeLocation', {}, 'Bytt lokasjon')}
        warehouseID={locationTarget?.warehouseID}
        minSearchLength={2}
      />
    </div>
  );
};

export default PutAwayOrders;
